import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search,
  DirectionsCar,
  CheckCircle,
  RadioButtonUnchecked,
  LocationOn,
  Schedule,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import api from "../../api/axiosInstance";
import { toDarkSafeChipColors } from "../../components/common/StatusChip";

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  order_received: { color: "#4A5568", bg: "#EDF2F7", icon: "📋" },
  payment_confirmed: { color: "#2B6CB0", bg: "#EBF8FF", icon: "💳" },
  vehicle_purchased: { color: "#553C9A", bg: "#E9D8FD", icon: "🚗" },
  awaiting_export_docs: { color: "#744210", bg: "#FEFCBF", icon: "📄" },
  shipped: { color: "#2C5282", bg: "#BEE3F8", icon: "📦" },
  in_transit: { color: "#276749", bg: "#C6F6D5", icon: "⛵" },
  arrived_port: { color: "#22543D", bg: "#C6F6D5", icon: "🏝️" },
  customs_clearance: { color: "#744210", bg: "#FEFCBF", icon: "🛃" },
  customs_cleared: { color: "#22543D", bg: "#C6F6D5", icon: "✅" },
  ready_for_collection: { color: "#702459", bg: "#FED7E2", icon: "🎉" },
  delivered: { color: "#22543D", bg: "#C6F6D5", icon: "✅" },
};

const getStatusCfg = (status) =>
  STATUS_CONFIG[status] || { color: "#4A5568", bg: "#EDF2F7", icon: "🔔" };

// ── Minimal public header ──────────────────────────────────────────────────────
const PublicHeader = () => (
  <Box
    sx={{
      bgcolor: "#001A3C",
      px: { xs: 2, md: 4 },
      py: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Box
      component="img"
      src="/logos/Waypoint-Imports-Favicon-White.png"
      alt="Waypoint Imports"
      sx={{
        width: 54,
        height: 40,
        objectFit: "contain",
        flexShrink: 0,
      }}
    />
    <Box>
      <Typography
        sx={{ color: "white", fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}
      >
        Waypoint Imports
      </Typography>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.5)",
          fontSize: 10,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
        }}
      >
        Vehicle Tracking
      </Typography>
    </Box>
  </Box>
);

// ── Search form ────────────────────────────────────────────────────────────────
const TrackingSearch = ({ onSearch, loading }) => {
  const [input, setInput] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) onSearch(input.trim().toUpperCase());
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", gap: 1.5, maxWidth: 520, mx: "auto" }}
    >
      <TextField
        fullWidth
        placeholder="Enter tracking ID  e.g. VHC-2026-0001"
        value={input}
        onChange={(e) => setInput(e.target.value.toUpperCase())}
        size="small"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "text.disabled", fontSize: 18 }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Button
        type="submit"
        variant="contained"
        disabled={!input.trim() || loading}
        sx={{ whiteSpace: "nowrap", px: 3, borderRadius: 2 }}
      >
        {loading ? <CircularProgress size={18} color="inherit" /> : "Track"}
      </Button>
    </Box>
  );
};

// ── Timeline ───────────────────────────────────────────────────────────────────
const StatusTimeline = ({ history, currentStatus }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const sorted = [...(history || [])].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
  );

  return (
    <Box>
      {sorted.map((entry, idx) => (
        <Box key={idx} sx={{ display: "flex", gap: 2, position: "relative" }}>
          {idx < sorted.length - 1 && (
            <Box
              sx={{
                position: "absolute",
                left: 15,
                top: 28,
                width: 2,
                height: "calc(100% - 8px)",
                bgcolor: idx === 0 ? "primary.main" : "divider",
              }}
            />
          )}
          <Box sx={{ flexShrink: 0, mt: 0.5, zIndex: 1 }}>
            {idx === 0 ? (
              <CheckCircle sx={{ fontSize: 30, color: "primary.main" }} />
            ) : (
              <RadioButtonUnchecked sx={{ fontSize: 30, color: "text.disabled" }} />
            )}
          </Box>
          <Box sx={{ pb: 3, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 0.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: idx === 0 ? 700 : 500,
                  // Was a hardcoded "#0E1420" for the non-current entry —
                  // that's a fixed dark-text color, so every past timeline
                  // entry rendered as near-invisible dark-on-dark once the
                  // page itself went dark. "text.primary" follows mode.
                  color: idx === 0 ? "primary.main" : "text.primary",
                }}
              >
                {entry.label || entry.status}
              </Typography>
              {entry.isCustom && (
                <Chip
                  label="Update"
                  size="small"
                  sx={{
                    fontSize: 9,
                    height: 16,
                    bgcolor: isDark ? "#2A1B4A" : "#E9D8FD",
                    color: isDark ? "#C4B5FD" : "#553C9A",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
            {entry.note && (
              <Typography variant="body2" color="text.secondary" mt={0.25}>
                {entry.note}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              mt={0.5}
            >
              {dayjs(entry.timestamp).format("MMMM D, YYYY [at] h:mm A")}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────
const VehicleTrackingPage = () => {
  const { trackingId: paramId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  const doSearch = async (id) => {
    setLoading(true);
    setError("");
    setVehicle(null);
    try {
      const { data } = await api.get(`/vehicles/track/${id}`);
      setVehicle(data.data?.vehicle || null);
      setSearched(true);
      // Update URL without reload
      window.history.replaceState(null, "", `/track/${id}`);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? `No vehicle shipment found with tracking ID "${id}". Please check and try again.`
          : "Something went wrong. Please try again.",
      );
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if tracking ID in URL
  useEffect(() => {
    if (paramId) doSearch(paramId.toUpperCase());
  }, [paramId]);

  const rawStatusCfg = vehicle ? getStatusCfg(vehicle.status) : null;
  // Status colors are a fixed light-pastel set (bg + matching text color) —
  // in dark mode, re-rendered as a deep tinted surface + bright tinted text
  // of the same hue (same transform StatusChip uses for admin-configured
  // status colors) instead of leaving a bright pastel banner on a dark page.
  const statusCfg =
    rawStatusCfg && isDark
      ? { ...rawStatusCfg, ...toDarkSafeChipColors(rawStatusCfg) }
      : rawStatusCfg;
  const eta = vehicle?.shipping?.estimatedArrivalDate;
  const actualArrival = vehicle?.shipping?.actualArrivalDate;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        fontFamily: '"Plus Jakarta Sans", sans-serif',
      }}
    >
      <PublicHeader />

      {/* Hero search band */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #001A3C 0%, #0D629B 100%)",
          px: { xs: 2, md: 4 },
          py: { xs: 4, md: 6 },
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontWeight: 700,
            fontSize: { xs: 22, md: 28 },
            mb: 1,
          }}
        >
          Track Your Vehicle Shipment
        </Typography>
        <Typography
          sx={{ color: "rgba(255,255,255,0.7)", mb: 3, fontSize: 15 }}
        >
          Enter your tracking ID to get real-time updates on your vehicle import
        </Typography>
        <TrackingSearch onSearch={doSearch} loading={loading} />
      </Box>

      {/* Content area */}
      <Box sx={{ maxWidth: 820, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* No result prompt */}
        {!vehicle && !error && !loading && !searched && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Box sx={{ fontSize: 52, mb: 2 }}>🚗</Box>
            <Typography fontWeight={700} fontSize={18} mb={1}>
              Track your vehicle import
            </Typography>
            <Typography color="text.secondary">
              Enter the tracking ID provided by Waypoint Imports to see the
              current status of your vehicle.
            </Typography>
          </Box>
        )}

        {/* Result */}
        {vehicle && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Current status banner */}
            <Box
              sx={{
                bgcolor: statusCfg.bg,
                border: `2px solid ${statusCfg.color}20`,
                borderRadius: 3,
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ fontSize: 40, lineHeight: 1 }}>{statusCfg.icon}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: statusCfg.color,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Current Status
                </Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: statusCfg.color,
                    lineHeight: 1.3,
                  }}
                >
                  {vehicle.statusLabel || vehicle.status}
                </Typography>
                {vehicle.statusHistory?.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: statusCfg.color, opacity: 0.8 }}
                  >
                    Last updated{" "}
                    {dayjs(
                      vehicle.statusHistory[vehicle.statusHistory.length - 1]
                        ?.timestamp,
                    ).fromNow?.() ||
                      dayjs(
                        vehicle.statusHistory[vehicle.statusHistory.length - 1]
                          ?.timestamp,
                      ).format("MMM D")}
                  </Typography>
                )}
              </Box>
              {(eta || actualArrival) && (
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      justifyContent: "flex-end",
                      mb: 0.25,
                    }}
                  >
                    <Schedule sx={{ fontSize: 14, color: statusCfg.color }} />
                    <Typography
                      variant="caption"
                      sx={{ color: statusCfg.color, fontWeight: 600 }}
                    >
                      {actualArrival ? "Arrived" : "Est. Arrival"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: statusCfg.color,
                      fontSize: 15,
                    }}
                  >
                    {dayjs(actualArrival || eta).format("MMM D, YYYY")}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Vehicle info card */}
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 2.5,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <DirectionsCar sx={{ color: "primary.main", fontSize: 22 }} />
                <Typography fontWeight={700} fontSize={15}>
                  Vehicle Details
                </Typography>
                <Box
                  sx={{
                    ml: "auto",
                    fontFamily: "monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "primary.main",
                    bgcolor: isDark ? "#17304D" : "#EBF4FF",
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {vehicle.trackingId}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                {[
                  { label: "Make", value: vehicle.vehicle?.make },
                  { label: "Model", value: vehicle.vehicle?.model },
                  { label: "Year", value: vehicle.vehicle?.year },
                  { label: "Color", value: vehicle.vehicle?.color },
                ].map((f) => (
                  <Box key={f.label}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        mb: 0.25,
                      }}
                    >
                      {f.label}
                    </Typography>
                    <Typography fontWeight={700} fontSize={14}>
                      {f.value || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                {[
                  {
                    label: "Origin",
                    value: vehicle.origin?.country,
                    icon: <LocationOn sx={{ fontSize: 14 }} />,
                  },
                  { label: "Vessel", value: vehicle.shipping?.vesselName },
                  { label: "Destination", value: vehicle.destination?.port },
                ].map((f) => (
                  <Box key={f.label}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        mb: 0.25,
                      }}
                    >
                      {f.label}
                    </Typography>
                    <Typography fontWeight={600} fontSize={13}>
                      {f.value || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Timeline */}
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 2.5,
              }}
            >
              <Typography fontWeight={700} fontSize={15} mb={2.5}>
                Shipment Timeline
              </Typography>
              <StatusTimeline
                history={vehicle.statusHistory}
                currentStatus={vehicle.status}
              />
            </Box>

            {/* Footer note — translucent tint of primary.main instead of a
                solid light-blue hex, so the theme-following text above it
                (color: "text.secondary") stays readable in dark mode too. */}
            <Box
              sx={{
                bgcolor: "rgba(13,98,155,0.08)",
                borderRadius: 2,
                p: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box sx={{ fontSize: 18, flexShrink: 0 }}>ℹ️</Box>
              <Typography
                variant="caption"
                color="text.secondary"
                lineHeight={1.6}
              >
                For questions about your vehicle shipment, contact{" "}
                <strong>Waypoint Imports</strong> with your tracking ID{" "}
                <strong>{vehicle.trackingId}</strong>.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          py: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.disabled">
          © {new Date().getFullYear()} Waypoint Imports · Grand Cayman Islands
        </Typography>
      </Box>
    </Box>
  );
};

export default VehicleTrackingPage;
